import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';
import { Component, ElementRef, Input, inject } from '@angular/core';
import { combineLatest, Observable, of, throwError, from } from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { catchError, filter, map, skipWhile, switchMap, take, tap } from 'rxjs/operators';

import { UserKeys } from '@config/keys/user.keys';

import { FormsState } from '@core/models/forms.model';
import { DoodUserModel } from '@store/authentication/authentication.model';

import { PatternUtil } from '@shared/utils/pattern/pattern.utils';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';

import { ComoService } from '@core/services/como/como.service';
import { UserService } from '@core/services/user/user.service';
import { PongoService } from '@core/services/pongo/pongo.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { ZerosixService } from '@core/services/zerosix/zerosix.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { AuthFirebaseService } from '@core/services/api/auth-firebase/auth-firebase.service';

import { emailValidator } from '@shared/validators/email/email.validator';
import { CountryISO } from '@shared/components/input-country-phone/config/country-phone.config';
import { AuthenticationUserJourneyType } from '@shared/blocks/authentication-block/authentication-user-journey-type';
import { AuthenticationModalComponent } from '@shared/modals/authentication-modal/authentication-modal.component';
import {
  AuthenticationForm,
  AuthenticationFormState,
} from '@common/states/authentication-form.state';
import { MarketplaceStoreRefiner } from '@common/refiners/marketplace.refiner';
import { ZerosixCreateOrLinkAccountModalComponent } from '@shared/modals/zerosix/zerosix-create-or-link-account-modal/zerosix-create-or-link-account-modal.component';
import { PongoCreateOrLinkAccountModalComponent } from '@shared/modals/pongo/pongo-create-or-link-account-modal/pongo-create-or-link-account-modal.component';
import { ComoCreateOrLinkAccountModalComponent } from '@shared/modals/como/como-create-or-link-account-modal/como-create-or-link-account-modal.component';
import { UserCredential } from 'firebase/auth';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { UnverifiedUserEmailAlertModalData } from '@shared/modals/unverified-user-email-alert-modal/unverified-user-email-alert-modal.component';
import { UntypedModalIO } from '@core/services/modals/modals-util.type';
import { AuthVerificationEmailService } from '@core/services/auth/auth-verification-email.service';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { isFamilyNameFirstLocale } from '@shared/utils/name/format-full-name.util';
import { reuseExistingAccountOrCreate$ } from './reuse-existing-account.util';

@Component({
  selector: 'app-authentication-signup-step',
  templateUrl: './authentication-signup-step.component.html',
  standalone: false,
})
export class AuthenticationSignupStepComponent extends DestroyerBase {
  @Input() legalCheckboxLabel?: string;
  @Input() optinCheckboxLabel?: string;
  @Input() userJourneyType = AuthenticationUserJourneyType.ASK_EMAIL_FIRST;
  @Input() signupStepHeading!: string;
  @Input() signupStepSubheading!: string;
  @Input() signupStepDisplayFirstname = true;
  @Input() signupStepFirstnameLabel!: string;
  @Input() signupStepFirstnameHelp!: string;
  @Input() signupStepDisplayLastname = true;
  @Input() signupStepLastnameLabel!: string;
  @Input() signupStepLastnameHelp!: string;
  @Input() signupStepEmailLabel!: string;
  @Input() signupStepEmailHelp!: string;
  @Input() signupStepDisplayPhone = true;
  @Input() signupStepPhoneLabel!: string;
  @Input() signupStepPhoneHelp!: string;
  @Input() signupStepPasswordLabel!: string;
  @Input() signupStepPasswordHelp!: string;
  @Input() signupFooter: unknown[] = [];
  @Input() cgvUrl?: string;
  @Input() privacyPolicyUrl?: string;
  @Input() legalNoticeUrl?: string;

  firebaseToken$ = this.authFirebaseService.firebaseToken$;
  authenticationUserJourneyType = AuthenticationUserJourneyType;
  passwordErrorMessage?: string;
  emailErrorMessage?: string;
  anyErrorMessage?: string;
  signupWithEmailAndPassword = false;
  phoneErrorMessage?: string;
  form?: UntypedFormGroup;
  isSignUpInProgress = false;

  marketplaceName$ = this.marketplaceSelector.selectMarketplaceName;
  authenticationEmailValue$ = this.authenticationForm.selectValue<string>('email');
  authenticationProviderValue$ = this.authenticationForm.selectValue<string>('provider');

  marketplaceCountry$ = this.marketplaceSelector.selectMarketplaceCountry;

  countryIso = CountryISO;

  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly settingsSelector = inject(SettingsStoreSelector);

  get isFamilyNameFirst(): boolean {
    return isFamilyNameFirstLocale(this.settingsSelector.app.locale);
  }

  constructor(
    private el: ElementRef,
    private readonly router: Router,
    private readonly fb: UntypedFormBuilder,
    private authSelector: AuthStoreSelector,
    private readonly comoService: ComoService,
    private readonly userService: UserService,
    private readonly pongoService: PongoService,
    private readonly modalsService: ModalsService<[UnverifiedUserEmailAlertModalData]>,
    private readonly zerosixService: ZerosixService,
    private readonly routerHelper: RouterHelperService,
    private authenticationForm: AuthenticationFormState,
    private readonly translateService: TranslateService,
    private marketplaceRefiner: MarketplaceStoreRefiner,
    private marketplaceSelector: MarketplaceStoreSelector,
    private readonly authFirebaseService: AuthFirebaseService,
    private readonly analyticsService: AnalyticsService,
    private readonly authVerificationEmailService: AuthVerificationEmailService,
  ) {
    super();
    if (this.cb2PreviewState.isActive()) {
      this.initSignupForm('password');
      return;
    }

    this.authenticationProviderValue$
      .pipe(
        skipWhile(value => !value),
        take(1),
      )
      .subscribe(value => this.initSignupForm(value ?? 'password'));
  }

  private initSignupForm(provider: string): void {
    this.signupWithEmailAndPassword = !(
      provider && ['google.com', 'facebook.com', 'apple.com'].includes(provider)
    );

    // TODO: Check this form
    if (provider === 'apple.com' && Capacitor.isNativePlatform()) {
      /* FIX APPLE NATIVE CONNECT WITHOUT FIRSTNAME & NAME */
      this.form = this.fb.group({
        provider: [''],
        firebaseUid: [''],
        [UserKeys.Email]: ['', Validators.required],
        [UserKeys.Phone]: ['', Validators.pattern(PatternUtil.phone)],
        ...(this.signupWithEmailAndPassword && {
          [UserKeys.Password]: ['', Validators.required],
        }),
        legals: [false, Validators.requiredTrue],
        newsletter: [false, Validators.required],
      });
    } else {
      this.form = this.fb.group({
        provider: [''],
        firebaseUid: [''],
        [UserKeys.Email]: [
          '',
          {
            validators: [emailValidator(), Validators.required],
            updateOn: 'blur',
          },
        ],
        [UserKeys.FirstName]: ['', Validators.required],
        [UserKeys.Phone]: [
          '',
          {
            validators: [Validators.pattern(PatternUtil.phone)],
            updateOn: 'blur',
          },
        ],
        [UserKeys.LastName]: [''],
        ...(this.signupWithEmailAndPassword && {
          [UserKeys.Password]: ['', Validators.required],
        }),
        legals: [false, Validators.requiredTrue],
        newsletter: [false, Validators.required],
      });
    }
    this.form.setValue({
      ...this.form.value,
      email: this.authenticationForm.value?.['email'] ?? '',
    });
    this.authenticationForm.upsert(this.form);
  }

  onSubmit(): void {
    if (!this.form?.valid) {
      return;
    }

    this.isSignUpInProgress = true;
    combineLatest([this.authFirebaseService.firebaseToken$, this.authenticationForm.select()])
      .pipe(
        take(1),
        switchMap(([token, form]) => {
          // Dood guest → create real Firebase account + PATCH upgrade-anonymous
          if (this.authFirebaseService.isAnonymous) {
            return this.upgradeAnonymousUser$(form);
          }
          // Real Firebase ID token present: reuse its DOOD account if it exists, else create it
          if (token && !this.authFirebaseService.hasDoodAnonymousSession()) {
            return this.reuseExistingAccountOrCreateUser$(form);
          }
          // Neither Firebase nor DOOD account exists yet
          return this.passwordSignUp$(form).pipe(
            take(1),
            switchMap(() => this.createUser$(form)),
          );
        }),
        tap(() => {
          // Track successful signup
          const provider = this.form?.value.provider || 'password';
          const providerMap: Record<string, 'email' | 'google' | 'apple' | 'facebook'> = {
            'password': 'email',
            'google.com': 'google',
            'apple.com': 'apple',
            'facebook.com': 'facebook',
          };
          const mappedProvider = providerMap[provider] || 'email';
          this.analyticsService.trackAuthSuccess('signup', mappedProvider);
        }),
        tap(() => this.redirectIfNeeded()),
        tap(() => this.modalsService.close<UntypedModalIO>(AuthenticationModalComponent.handle)),
        tap(user => this.displayLoyaltyAccountPrompt(user)),
        tap(user => {
          this.checkEmailVerificationStatus(
            user,
            this.authVerificationEmailService.isUserEmailVerificationPluginActive(),
          );
        }),
      )
      .subscribe();
  }

  closeModal(): void {
    this.modalsService.close<UntypedModalIO>(AuthenticationModalComponent.handle);
  }

  onUpdatePhone($event: string): void {
    this.form?.patchValue({ [UserKeys.Phone]: $event.replace(/\s/g, '') });
  }

  private passwordSignUp$(form: any): Observable<FormsState['authentication']> {
    if (
      ['google.com', 'facebook.com', 'apple.com'].includes(form.provider) ||
      this.authFirebaseService.isAnonymous
    ) {
      return of(form);
    }

    return from(this.authFirebaseService.signUp(form.email.trim(), form.password)).pipe(
      catchError((error: any) => {
        this.isSignUpInProgress = false;

        this.passwordErrorMessage = this.authFirebaseService.getPasswordErrorMessage(error);

        if (this.passwordErrorMessage) {
          this.scrollToFirstInvalidControl('password');
          this.analyticsService.trackErrorDisplay(
            'password_weak',
            error.code,
            this.passwordErrorMessage,
            'auth_signup',
          );
        }

        if (!this.passwordErrorMessage) {
          this.emailErrorMessage = this.authFirebaseService.getEmailErrorMessage(error);
          if (this.emailErrorMessage) {
            this.scrollToFirstInvalidControl('email');
            this.analyticsService.trackErrorDisplay(
              error.code === 'auth/email-already-in-use' ? 'account_exists' : 'email_format',
              error.code,
              this.emailErrorMessage,
              'auth_signup',
            );
          }
        }

        if (!this.passwordErrorMessage && !this.emailErrorMessage) {
          this.anyErrorMessage = error.message;
          this.analyticsService.trackErrorDisplay(
            'generic_error',
            error.code,
            error.message,
            'auth_signup',
          );
        }

        this.analyticsService.trackAuthFailure('signup', 'email', error.code, error.message);

        return throwError(() => error);
      }),
      tap((userCredential: UserCredential) =>
        this.form?.controls.firebaseUid.setValue(userCredential.user?.uid),
      ),
      switchMap((userCredential: UserCredential) =>
        this.authFirebaseService.saveFirebaseTokenInSessionStore$(userCredential),
      ),
      map(() => form),
    );
  }

  private reuseExistingAccountOrCreateUser$(form: AuthenticationForm) {
    return reuseExistingAccountOrCreate$(
      this.authFirebaseService.loadUser$(),
      () => this.createUser$(form),
      existingUser =>
        this.analyticsService.log({
          level: 'warn',
          label: 'signup_existing_account_reused',
          message: { userId: existingUser.id },
        }),
    );
  }

  private createUser$(form: AuthenticationForm) {
    const navigatorLocale = navigator ? [...navigator.languages] : ['fr-FR'];
    if (!form) return of(null);

    let newUser = {
      [UserKeys.Optin]: Boolean(form.newsletter),
      [UserKeys.Phone]: form.phone.toString(),
      [UserKeys.LastName]: form.lastname.toString(),
      [UserKeys.Email]: form.email.toString().trim(),
      [UserKeys.FirstName]: form.firstname.toString(),
      [UserKeys.PreferredLocales]: navigatorLocale,
      [UserKeys.FirebaseUID]: this.authFirebaseService.getCurrentUser()?.uid,
    };

    if (form.provider === 'apple.com' && Capacitor.isNativePlatform()) {
      newUser = {
        ...newUser,
        [UserKeys.FirstName]: form.email.toString(),
      };
    }

    return this.userService.createUser$(newUser).pipe(
      catchError((error: any) => {
        this.isSignUpInProgress = false;
        this.anyErrorMessage = this.translateService.instant('authentication.unknown-error');
        return throwError(() => error);
      }),
    );
  }

  private upgradeAnonymousUser$(form: AuthenticationForm): Observable<DoodUserModel> {
    if (!form) {
      throw new Error('[AuthenticationSignupStepComponent] authentication form is empty !');
    }

    const newUser = {
      [UserKeys.Email]: form.email.toString(),
      [UserKeys.Phone]: form.phone.toString(),
      [UserKeys.Optin]: Boolean(form.newsletter),
      [UserKeys.LastName]: form.lastname.toString(),
      [UserKeys.FirstName]: form.firstname.toString(),
    };

    return this.authFirebaseService
      .upgradeAnonymousWithEmailAndPassword(form.email.toString(), form.password.toString())
      .pipe(
        filter((firebaseIdToken): firebaseIdToken is string => !!firebaseIdToken),
        switchMap(firebaseIdToken =>
          this.authSelector.selectUser$.pipe(
            take(1),
            switchMap(user =>
              this.userService.upgradeAnonymousUser$(newUser, user?.id ?? '', firebaseIdToken),
            ),
          ),
        ),
      );
  }

  private redirectIfNeeded(): void {
    const requestedUrl = this.authSelector.selectGuard()?.requestedUrl;
    if (requestedUrl) {
      this.router.navigate([this.routerHelper.translateRoute(requestedUrl)]);
    }
  }

  private displayLoyaltyAccountPrompt(loggedInUser?: DoodUserModel | null): void {
    combineLatest([
      this.marketplaceRefiner.selectIsComoPluginEnabled,
      this.comoService.isUserComoAccountEnabledOnMarketplaceLevel$(),
    ])
      .pipe(
        take(1),
        tap(([isMarketplaceComoEnabled, isUserComoEnabled]) => {
          if (
            isMarketplaceComoEnabled &&
            !isUserComoEnabled &&
            !this.authFirebaseService.isAnonymous
          ) {
            this.modalsService.open<UntypedModalIO>(ComoCreateOrLinkAccountModalComponent.handle);
          }
        }),
      )
      .subscribe();

    combineLatest([
      this.marketplaceRefiner.selectIsZeroSixPluginEnabled,
      this.zerosixService.isUserZeroSixCardEnabledOnMarketplaceLevel$(),
    ])
      .pipe(
        take(1),
        tap(([isMarketplaceComoEnabled, isUserZerosixEnabled]) => {
          if (
            isMarketplaceComoEnabled &&
            !isUserZerosixEnabled &&
            !this.authFirebaseService.isAnonymous
          ) {
            this.modalsService.open<UntypedModalIO>(
              ZerosixCreateOrLinkAccountModalComponent.handle,
            );
          }
        }),
      )
      .subscribe();

    this.marketplaceRefiner.selectIsPongoPluginEnabled
      .pipe(
        take(1),
        tap(isMarketplacePongoEnabled => {
          const user = loggedInUser ?? this.authSelector.selectUser();
          const isAnonymous = user?.[UserKeys.IsAnonymous] ?? this.authFirebaseService.isAnonymous;
          if (
            this.pongoService.shouldOpenCreateOrLinkModal(
              user,
              isMarketplacePongoEnabled,
              isAnonymous,
            )
          ) {
            this.modalsService.open<UntypedModalIO>(PongoCreateOrLinkAccountModalComponent.handle);
          }
        }),
      )
      .subscribe();
  }

  private scrollToFirstInvalidControl(type: string): void {
    if (type === 'email') {
      const firstInvalidControl: HTMLElement = this.el.nativeElement.querySelector('#email');
      firstInvalidControl?.focus();
    }

    if (type === 'password') {
      const firstInvalidControl: HTMLElement = this.el.nativeElement.querySelector('#password');
      firstInvalidControl?.focus();
    }
  }

  private checkEmailVerificationStatus(
    user: DoodUserModel | null,
    isUserEmailVerificationPluginActive: boolean,
  ): void {
    if (!isUserEmailVerificationPluginActive) {
      return;
    }
    if (!user) {
      return;
    }
    if (user.email_verified) {
      return;
    }
    this.modalsService.open('unverified-user-email-alert-modal');
  }
}
